import React from 'react';
import { colors, spacing, typography } from '../../theme/tokens';

/**
 * Table Component System
 *
 * A complete table component with support for striped rows, borders, and hover states.
 *
 * Usage:
 * <Table striped bordered hover>
 *   <TableHead>
 *     <TableRow>
 *       <TableCell header>Column 1</TableCell>
 *       <TableCell header>Column 2</TableCell>
 *     </TableRow>
 *   </TableHead>
 *   <TableBody>
 *     <TableRow>
 *       <TableCell>Data 1</TableCell>
 *       <TableCell>Data 2</TableCell>
 *     </TableRow>
 *   </TableBody>
 * </Table>
 */

// Main Table Container
export const Table = ({
  children,
  striped = false,
  bordered = true,
  hover = false,
  size = 'md',
  className = '',
}) => {
  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: size === 'sm' ? typography.fontSize.xs : typography.fontSize.sm,
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={tableStyle}
        className={className}
        data-striped={striped}
        data-bordered={bordered}
        data-hover={hover}
      >
        {children}
      </table>
    </div>
  );
};

// Table Head
export const TableHead = ({ children, className = '' }) => {
  return <thead className={className}>{children}</thead>;
};

// Table Body
export const TableBody = ({ children, className = '' }) => {
  return <tbody className={className}>{children}</tbody>;
};

// Table Row
export const TableRow = ({ children, onClick, className = '' }) => {
  const table = React.useContext(TableContext);

  const rowStyle = {
    borderBottom: table?.bordered ? `1px solid ${colors.border.default}` : 'none',
    backgroundColor: 'transparent',
    cursor: onClick ? 'pointer' : 'default',
  };

  return (
    <tr
      style={rowStyle}
      onClick={onClick}
      className={className}
    >
      {children}
    </tr>
  );
};

// Table Cell
export const TableCell = ({
  children,
  header = false,
  align = 'left',
  width,
  className = '',
}) => {
  const cellStyle = {
    padding: spacing.md,
    textAlign: align,
    color: header ? '#FFB84D' : colors.text.primary,
    fontWeight: header ? typography.fontWeight.semibold : typography.fontWeight.normal,
    fontSize: header ? typography.fontSize.xs : typography.fontSize.sm,
    width: width || 'auto',
    borderBottom: header ? `1px solid ${colors.border.default}` : 'none',
  };

  const Cell = header ? 'th' : 'td';

  return (
    <Cell style={cellStyle} className={className}>
      {children}
    </Cell>
  );
};

// Context for passing table props to rows
const TableContext = React.createContext(null);

// Enhanced Table with Context
export const TableWithContext = ({
  children,
  striped = false,
  bordered = true,
  hover = false,
  size = 'md',
  className = '',
}) => {
  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: size === 'sm' ? typography.fontSize.xs : typography.fontSize.sm,
  };

  const contextValue = { striped, bordered, hover, size };

  return (
    <TableContext.Provider value={contextValue}>
      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle} className={className}>
          {children}
        </table>
      </div>
    </TableContext.Provider>
  );
};

// Default export is the enhanced table
export default TableWithContext;
